import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Slide, useTheme, alpha } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { DEFAULT_THEME } from '../../constants/themeDefaults';
import { offlineService, OFFLINE_EVENTS } from '../../services/offlineService';

const OfflineBanner: React.FC = () => {
  const theme = DEFAULT_THEME;
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  
  useEffect(() => {
    const handleOnlineStatusChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setIsOffline(!customEvent.detail.isOnline);
      
      // If we're back online, reset dismissed flag for next offline occurrence
      if (customEvent.detail.isOnline) {
        setIsDismissed(false);
      }
    };
    
    // Listen to our custom offline service events
    offlineService.addEventListener(
      OFFLINE_EVENTS.CONNECTIVITY_CHANGED,
      handleOnlineStatusChange
    );
    
    // Also listen to native online/offline events as backup
    const handleNativeOnline = () => setIsOffline(false);
    const handleNativeOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleNativeOnline);
    window.addEventListener('offline', handleNativeOffline);
    
    return () => {
      offlineService.removeEventListener(
        OFFLINE_EVENTS.CONNECTIVITY_CHANGED, 
        handleOnlineStatusChange
      );
      window.removeEventListener('online', handleNativeOnline);
      window.removeEventListener('offline', handleNativeOffline);
    };
  }, []);
  
  const handleDismiss = () => {
    setIsDismissed(true);
  };
  
  // Only show the banner if we're offline and it hasn't been dismissed
  const showBanner = isOffline && !isDismissed;
  
  if (showBanner) {
    return (
      <Slide direction="down" in={showBanner} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            backgroundColor: alpha(theme.accentColor, 0.95),
            color: theme.secondaryColor,
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WifiOffIcon sx={{ mr: 1.5 }} />
            <Typography variant="body1">
              You're offline. Changes will be saved and synced when you reconnect.
            </Typography>
          </Box>
          <IconButton
            size="small"
            edge="end"
            color="inherit"
            aria-label="dismiss"
            onClick={handleDismiss}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Slide>
    );
  }
  
  return null;
};

export default OfflineBanner; 